import {
  Column,
  DataStatus,
  Label,
  Table,
  TableRow,
  TableRowAction,
  showNotification,
} from 'platform/components';
import {Box, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useCallback} from 'react';

import {isNotNil} from 'ramda';

import {
  SchedulerLatestRunResponseBody,
  useDeleteLatestSchedulerRunMutation,
  useListLatestSchedulerRunsQuery,
  useRunSchedulerMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError, Main} from '@dms/shared';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

const PAGE_TITLE = i18n.t('page.demo.customerMessageSchedules');

const COLUMNS: Column[] = [
  {
    element: <Label>{i18n.t('general.labels.name')}</Label>,
    width: '35%',
  },
  {
    element: <Label>{i18n.t('page.demo.customerMessageSchedules.scheduledFor')}</Label>,
    width: '30%',
  },
  {
    element: <Label>{i18n.t('page.demo.customerMessageSchedules.lastRanAt')}</Label>,
    width: '30%',
  },
];

export function CustomerMessageSchedules() {
  const formatDateTime = useDateTimeFormatter();
  const {data, isLoading, isError} = useListLatestSchedulerRunsQuery();
  const [deleteLatestSchedulerRun] = useDeleteLatestSchedulerRunMutation();
  const [runScheduler] = useRunSchedulerMutation();

  const mapSchedulerRunActions = useCallback(
    (schedulerRun: SchedulerLatestRunResponseBody): {secondary: TableRowAction[]} => ({
      secondary: [
        {
          title: i18n.t('general.actions.retry'),
          icon: 'action/update',
          onClick: () =>
            runScheduler({schedulerName: schedulerRun.name})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyCompleted'))
              )
              .catch(handleApiError),
        },
        {
          title: i18n.t('general.actions.delete'),
          icon: 'action/delete',
          severity: 'danger',
          onClick: () =>
            deleteLatestSchedulerRun({schedulerName: schedulerRun.name})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .catch(handleApiError),
        },
      ],
    }),
    [deleteLatestSchedulerRun, runScheduler]
  );

  return (
    <Main isFullHeight heading={PAGE_TITLE}>
      <DataStatus isLoading={isLoading} isError={isError} isEmpty={!data?.runs.length}>
        <Box maxWidth="100%">
          <Table columns={COLUMNS}>
            {data?.runs.map((schedulerRun) => (
              <TableRow
                key={schedulerRun.name}
                actions={mapSchedulerRunActions(schedulerRun)}
                actionsWidth="1%"
              >
                <Text size="small">{schedulerRun.name}</Text>
                <Text size="small">
                  {formatDateTime('dateTimeShort', parseDate(schedulerRun.scheduledFor))}
                </Text>
                <Text size="small">
                  {isNotNil(schedulerRun.lastRanAt)
                    ? formatDateTime('dateTimeShort', parseDate(schedulerRun.lastRanAt))
                    : EMPTY_PLACEHOLDER}
                </Text>
              </TableRow>
            ))}
          </Table>
        </Box>
      </DataStatus>
    </Main>
  );
}
